import React, { useEffect, useState } from "react";
import { Plus, Copy, ShieldCheck, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout from "@/components/MerchantLayout";
import { StatusPill } from "@/components/Layout";
import { mApi } from "@/lib/api";

export default function MerchantApiKeys() {
  const [items, setItems] = useState([]);
  const [revealed, setRevealed] = useState(null);

  const load = () => mApi.get("/merchant/api-keys").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await mApi.post("/merchant/api-keys");
      setRevealed(r.data);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Existing integrations will stop working.")) return;
    await mApi.post(`/merchant/api-keys/${id}/revoke`);
    toast.success("Revoked");
    load();
  };

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-apikeys-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl tracking-tight font-medium">API Keys</h1>
            <p className="mt-1 text-sm text-zinc-500">Server-to-server credentials for the Blonix Proxy API.</p>
          </div>
          <button onClick={create} data-testid="m-create-key-btn" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150">
            <Plus size={14} weight="bold" /> Create new key
          </button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Label</th>
                <th className="px-5 py-3">Key</th>
                <th className="px-5 py-3">Whitelist</th>
                <th className="px-5 py-3">HMAC</th>
                <th className="px-5 py-3 text-right">Rate / min</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Last used</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-medium">{k.label || "primary"}</td>
                  <td className="px-5 py-3 font-mono text-xs">{k.masked}</td>
                  <td className="px-5 py-3 text-xs">{(k.ip_whitelist || []).length || <span className="text-zinc-400">none</span>}</td>
                  <td className="px-5 py-3"><StatusPill value={k.require_hmac ? "active" : "pending"} /></td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{k.rate_limit_per_min}</td>
                  <td className="px-5 py-3"><StatusPill value={k.revoked ? "suspended" : "active"} /></td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{k.last_used_at ? k.last_used_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    {!k.revoked && (
                      <button onClick={() => revoke(k.id)} data-testid={`m-revoke-${k.id}`} className="text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-xs text-zinc-500">No keys yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>

        {revealed && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-950/50" onClick={() => setRevealed(null)}>
            <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                <div className="font-display text-lg tracking-tight font-medium">New API key</div>
                <button onClick={() => setRevealed(null)} className="text-zinc-500 hover:text-zinc-900"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-zinc-600">This key will only be shown once. Store it securely now.</p>
                <div className="bg-zinc-950 text-emerald-300 font-mono text-xs p-4 rounded-sm break-all" data-testid="m-revealed-key">{revealed.api_key}</div>
                <button
                  onClick={() => { navigator.clipboard.writeText(revealed.api_key); toast.success("Copied"); }}
                  data-testid="m-copy-key-btn"
                  className="inline-flex items-center gap-2 h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}
